import React, { useEffect, useRef, useState } from 'react';
import { Grid, Box, Typography, Button } from '@mui/material';
import CustomTextField from '../../../../components/CustomTextField/CustomTextField';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { UserService } from 'services/UserService';
import { useAuth } from 'context/authContext';
import CircularProgress from '@mui/material/CircularProgress';

const NameAndPronounsFields = ({
  onChange = () => { },
  initialValues,
  setShowIcons,
}: any) => {
  const { currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [filePreview, setFilePreview] = useState<string | null>(
    initialValues?.profileUrl || null
  );
  const [formValues, setFormValues] = useState(initialValues);
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    onChange(formValues);

    // Check if the fields are filled and toggle the showIcons state
    setShowIcons((prevState: any) => ({
      ...prevState,
      nameAndPronouns: !!(
        formValues.firstName &&
        formValues.lastName &&
        formValues.pronouns
      ),
    }));
  }, [formValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prevValues: any) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  // Handle changing profile picture
  const handleUpdatePicture = () => {
    // First, clear the current file preview
    setFilePreview(null);
  };

  const handleUploadProfile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);

      try {
        const downloadURL = await UserService.uploadProfilePicture(
          currentUser.uid,
          file
        );
        setFilePreview(downloadURL); // Display uploaded image

        // Update the formValues to include profileUrl
        setFormValues((prevValues: any) => ({
          ...prevValues,
          profileUrl: downloadURL,
        }));
      } catch (error) {
        console.error('Error uploading profile picture:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false); // Ensure loading ends if no file is selected
    }
  };

  const handleDeletePicture = () => {
    setFilePreview(null); // Remove the preview image
    setFileName(''); // Clear the file name

    // Update form values to remove the profileUrl
    setFormValues((prevValues: any) => ({
      ...prevValues,
      profileUrl: null, // Or '' depending on how you handle an empty state
    }));
  };

  return (
    <Grid
      display={'flex'}
      gap={3}
      flexDirection={'column'}
      p={{ xs: 2, md: 5 }}
      mt={{ xs: 3, md: 0 }}>
      <Grid
        item
        xs={12}
        sm={6}
        display={'flex'}
        gap={1}
        flexDirection={'column'}>
        <label htmlFor='firstName' style={styleSheet.labelText}>
          First Name
        </label>
        <Grid display={'flex'} gap={2} alignItems={'center'} flex={1}>
          <Box sx={{ flexGrow: 1 }}>
            <CustomTextField
              name='firstName'
              sx={{ width: '100%', maxWidth: '654px' }}
              value={formValues?.firstName}
              onChange={handleChange}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        display={'flex'}
        gap={1}
        flexDirection={'column'}>
        <label htmlFor='lastName' style={styleSheet.labelText}>
          Last Name
        </label>
        <Grid display={'flex'} gap={2} alignItems={'center'} flex={1}>
          <Box sx={{ flexGrow: 1 }}>
            <CustomTextField
              name='lastName'
              sx={{ width: '100%', maxWidth: '654px' }}
              value={formValues?.lastName}
              onChange={handleChange}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        display={'flex'}
        gap={1}
        flexDirection={'column'}>
        <label htmlFor='pronouns' style={styleSheet.labelText}>
          Pronouns
        </label>
        <Box sx={{ flexGrow: 1 }}>
          <CustomTextField
            name='pronouns'
            sx={{ width: '100%', maxWidth: '654px' }}
            value={formValues?.pronouns}
            onChange={handleChange}
          />
        </Box>
      </Grid>
      <Grid item xs={12} sm={8} md={6} lg={4}>
        <Typography
          variant='h6'
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: '18px',
            color: '#000000',
            marginBottom: 2,
          }}>
          Please upload your profile picture
        </Typography>
        <Box sx={styleSheet.container}>
          {!filePreview ? (
            <Box sx={styleSheet.downContainer}>
              <CloudUploadIcon sx={{ fontSize: 40, color: '#666' }} />
              <Typography variant='body1' sx={{ marginTop: 1 }}>
                Drag & Drop or choose file to upload
              </Typography>
              <Button
                variant='contained'
                sx={styleSheet.browseFile}
                onClick={handleUploadProfile}
                disabled={isLoading}>
                {isLoading ? <CircularProgress size={24} /> : 'Browse File'}
              </Button>
              <input
                type='file'
                accept='image/*'
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </Box>
          ) : (
            <Box
              display={'flex'}
              mt={2}
              gap={6}
              flexDirection={{ xs: 'column', md: 'row' }}
              justifyContent={{ xs: 'start', md: 'center' }}
              alignItems={{ xs: 'start', md: 'center' }}>
              <img
                src={filePreview}
                alt='Selected'
                style={{ ...styleSheet.img }}
              />
              <Box
                display={'flex'}
                gap={2}
                justifyContent={'center'}
                alignItems={'center'}>
                <Button sx={styleSheet.changeBtn} onClick={handleUpdatePicture}>
                  Change picture
                </Button>
                <Button sx={styleSheet.deleteBtn} onClick={handleDeletePicture}>
                  Delete picture
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default NameAndPronounsFields;

const styleSheet = {
  labelText: {
    fontWeight: 400,
    fontSize: '17px',
    color: '#000000',
  },

  downContainer: {
    border: '2px dashed #ccc',
    width: { xs: '295px', md: '620px' },
    height: '276px',
    borderRadius: '8px',
    padding: '16px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    backgroundColor: '#f9f9f9',
    paddingBottom: '16px',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth: '654px',
  },
  changeBtn: {
    width: '131px',
    height: '37px',
    p: '6px 20px',
    backgroundColor: '#233F91',
    borderRadius: '15px',
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#233F91',
      color: 'white',
    },
  },
  deleteBtn: {
    width: '129px',
    height: '37px',
    p: '6px 20px',
    backgroundColor: '#FFF3F3',
    border: '1px solid #FFDBDB',
    borderRadius: '15px',
    color: '#FF3535',
    fontSize: '14px',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#FFF3F3',
      color: '#FF3535',
    },
  },
  img: {
    width: '136.98px',
    height: '137.09px',
    objectFit: 'cover' as 'cover',
    border: '3.19px solid #BFBFBF',
    borderRadius: '50%',
  },
  browseFile: {
    marginTop: 2,
    backgroundColor: '#233F91',
    color: '#fff',
    borderRadius: '15px',
    width: '215px',
    height: '57px',
    mt: 2,
  },
};
